import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {TableColumnSettings} from "../../services/table-column-manager";
import {TableHeaderDefDirective, TableRowDefDirective} from "../../directives/resizable-table-defs.directive";

const DEFAULT_ROW_HEIGHT = 48;
const DEFAULT_HEADER_HEIGHT = 33;
const FETCH_NEXT_SCROLL_THRESHOLD = 50;

/**
 * One shared table: virtual scroll, empty state, optional column resize/reorder.
 *
 * The caller owns the markup: project a `tableHeader` template with the `.th` cells and a
 * `tableRow` template with the `.td` cells, both keyed by the same `col-<name>` classes as
 * `defaultWidths`. `[resizable]="false"` skips TableColumnManager - no resize handles, no
 * drag-reorder.
 *
 *   <app-bringoz-data-table [uniqueId]="tableUniqueId" [defaultWidths]="defaultWidths"
 *                           [items]="items" [resizable]="true" (fetchNext)="loadNextPage()">
 *     <ng-template tableHeader>
 *       <div class="th col-status text-ellipsis">{{ 'Status' | translate }}</div>
 *     </ng-template>
 *     <ng-template tableRow let-item>
 *       <div class="tr layout-row layout-align-start-center">
 *         <div class="td col-status layout-row layout-align-start-center">{{ item.status }}</div>
 *       </div>
 *     </ng-template>
 *     <app-empty-state tableEmptyState text="Nothing found"></app-empty-state>
 *   </app-bringoz-data-table>
 *
 * Paging is the caller's too: this component only reports that the body is scrolled near its
 * end through `fetchNext`, and renders whatever `items` it is given back. Every table here
 * loads from a server-paginated source, so who holds the cursor and whether a new page is
 * appended or replaces the list is a decision that belongs with that source, not here.
 */
require('./bringoz-data-table.scss');
@Component({
  standalone: false,
  selector: 'app-bringoz-data-table',
  templateUrl: './bringoz-data-table.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BringozDataTableComponent implements OnChanges, AfterContentInit {

  /** Scopes the injected column stylesheet and the `table-<uniqueId>` class - unique per table instance. */
  @Input() uniqueId: string;
  @Input() items: any[] = [];
  /** Key the row id is read from, for cdkVirtualFor's trackBy. */
  @Input() idField: string = 'id';
  @Input() rowHeight: number;
  @Input() headerHeight: number;
  @Input() isLoading: boolean;
  @Input() hideWhenEmpty: boolean = false;
  /** When false, columns stay at their CSS widths — no resize handle, no drag-reorder. */
  @Input() resizable: boolean = true;
  /** Initial width per column class, e.g. {'col-status': 120} - keyed like the projected cells. */
  @Input() defaultWidths: { [key: string]: number } = {};
  @Input() settings: TableColumnSettings = {columnSizes: {}, columnOrder: []};
  @Output() settingsChange = new EventEmitter<TableColumnSettings>();

  @Output() fetchNext = new EventEmitter<void>();

  @ContentChild(TableHeaderDefDirective) headerDef: TableHeaderDefDirective;
  @ContentChild(TableRowDefDirective) rowDef: TableRowDefDirective;

  @ViewChild(CdkVirtualScrollViewport, {static: false})
  private viewport: CdkVirtualScrollViewport;

  // Scroll fires many times while the viewport is still near the bottom. Without this, the same
  // page is requested twice before isLoading / the next cursor come back.
  private fetchNextIssued = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items || (changes.isLoading && !this.isLoading)) {
      this.fetchNextIssued = false;
    }
    if (changes.items) {
      // ngOnChanges runs before Angular writes this component's own template bindings to the
      // DOM, so on the very first items arriving together with hideWhenEmpty flipping [hidden]
      // false, this synchronous call still measures a display:none element (0 height) - CDK
      // then renders almost no rows and never re-measures on its own afterwards (it only listens
      // for window resize, not for an ancestor's visibility changing). The synchronous call is
      // kept for tables that never toggle [hidden] and get a correctly-sized element right away;
      // the microtask-deferred one re-measures once this cycle's DOM writes have landed.
      this.viewport?.checkViewportSize();
      Promise.resolve().then(() => this.viewport?.checkViewportSize());
    }
  }

  // Both templates are required, and a missing one renders as an empty header or an empty body -
  // which reads as "no data" rather than as a wiring mistake. Say so instead.
  ngAfterContentInit() {
    if (!this.headerDef || !this.rowDef) {
      console.warn(
        `app-bringoz-data-table (${this.uniqueId}): missing ` +
        `${!this.headerDef ? '<ng-template tableHeader>' : ''}` +
        `${!this.headerDef && !this.rowDef ? ' and ' : ''}` +
        `${!this.rowDef ? '<ng-template tableRow>' : ''} - that part of the table stays empty.`
      );
    }
  }

  get tableClass(): string {
    return `table-${this.uniqueId}`;
  }

  get resolvedRowHeight(): number {
    return this.rowHeight || DEFAULT_ROW_HEIGHT;
  }

  get resolvedHeaderHeight(): number {
    return this.headerHeight || DEFAULT_HEADER_HEIGHT;
  }

  get rowHeightPx(): string {
    return `${this.resolvedRowHeight}px`;
  }

  get headerHeightPx(): string {
    return `${this.resolvedHeaderHeight}px`;
  }

  get showEmptyState(): boolean {
    return !this.isLoading && !this.items?.length;
  }

  // Falls back to the index when the row has no id: an undefined key for every row would make
  // cdkVirtualFor treat all rows as the same item.
  trackByFn = (index: number, item: any) => item?.[this.idField] ?? index;

  onSettingsChange(settings: TableColumnSettings) {
    // Not reassigned to this.settings on purpose: the column manager mutates the object it was
    // given, and handing a new reference back down would make it re-apply its own change.
    this.settingsChange.emit(settings);
  }

  onBodyScroll() {
    if (!this.viewport || this.isLoading || this.fetchNextIssued) {
      return;
    }
    if (this.viewport.measureScrollOffset('bottom') < FETCH_NEXT_SCROLL_THRESHOLD) {
      this.fetchNextIssued = true;
      this.fetchNext.emit();
    }
  }

}
